import { IconType } from 'react-icons';
import { GenIcon, IconTree } from 'react-icons/lib';

// ---------------------------------------------------------------------------
// Icons drawn for OCIF in Phosphor's style, for shapes the pack doesn't have.
// They join the bundled Phosphor maps under bare names, so <Icon icon="…" />
// and the icon picker treat them like any other Phosphor icon.
//
// Drawn on Phosphor's grid: a 256×256 viewBox, 16-unit round strokes, and for
// duotone a 20% opacity fill behind the outline. Names must not collide with
// a Phosphor icon (phosphor.ts lets Phosphor win if one ever does).
// ---------------------------------------------------------------------------

interface CustomIconDrawing {
  /**
   * Areas filled at 20% opacity in the duotone weight. Shapes drawn clockwise
   * fill (overlaps stay one tint); one drawn counter-clockwise inside them cuts a hole.
   */
  fill: string;
  /** Filled shapes in both weights, for outlines reused from Phosphor (whose icons are filled, not stroked). */
  solid?: string;
  /** The outline, stroked in both weights. */
  stroke?: string;
  /** Fine details, stroked thinner than the outline in both weights. */
  detail?: string;
}

// A bank with a swept roof, paired columns and a dollar sign. Badged variants
// put their badge in the lower right, so they shorten the right-hand columns
// and the base lines to keep a clear gap around it.
const BANK_ROOF = 'M24,88V80C72,68,100,52,128,24C156,52,184,68,232,80v8Z';
const BANK_DOLLAR = 'M144,124H122a11,11,0,0,0,0,22h12a11,11,0,0,1,0,22H112M128,112v12M128,168v8';

interface BankTrim {
  /** Where the inner and outer right-hand columns end. */
  rightColumnsTo: [number, number];
  /** Where both base lines end. */
  baseTo: number;
}

const bank = ({ rightColumnsTo: [inner, outer], baseTo }: BankTrim) =>
  [BANK_ROOF, BANK_DOLLAR, `M40,112V176M72,112V176M184,112V${inner}M216,112V${outer}`, `M32,200H${baseTo}M16,232H${baseTo}`].join('');

// The financial report's title line, pie chart and dollar sign, shared by its badged variants.
const REPORT_HEADER =
  'M104,44H176M80,90a26,26,0,1,0,52,0a26,26,0,1,0,-52,0ZM106,64V90L126.8,105.6M193,70H173a10,10,0,0,0,0,20h10a10,10,0,0,1,0,20H163M178,62v8M178,110v8';
const REPORT_PIE_SLICE = 'M106,90V64A26,26,0,0,1,126.8,105.6Z';

// The badged reports stop the page edges short of the lower-right badge and
// drop the text lines to move the bars left.
const BADGED_REPORT_PAGES = 'M224,155.6V28a8,8,0,0,0,-8,-8H64a8,8,0,0,0,-8,8V202a8,8,0,0,0,8,8H146.3M56,44H40a8,8,0,0,0,-8,8V226a8,8,0,0,0,8,8H155.1';
const BADGED_REPORT_FILL = 'M32,44H56V210H149.7V234H32Z' + REPORT_PIE_SLICE;
const BADGED_REPORT_CONTENT = REPORT_HEADER + 'M80,186H148M96,186V164M120,186V150M144,186V136';

// Phosphor's HandCoins hand, without its coins, and its tinted cuff.
const HAND =
  'M230.33,141.06a24.43,24.43,0,0,0-21.24-4.23l-41.84,9.62A28,28,0,0,0,140,112H89.94a31.82,31.82,0,0,0-22.63,9.37L44.69,144H16A16,16,0,0,0,0,160v40a16,16,0,0,0,16,16H120a7.93,7.93,0,0,0,1.94-.24l64-16a6.94,6.94,0,0,0,1.19-.4L226,182.82l.44-.2a24.6,24.6,0,0,0,3.93-41.56ZM16,160H40v40H16Zm203.43,8.21-38,16.18L119,200H56V155.31l22.63-22.62A15.86,15.86,0,0,1,89.94,128H140a12,12,0,0,1,0,24H112a8,8,0,0,0,0,16h32a8.32,8.32,0,0,0,1.79-.2l67-15.41.31-.08a8.6,8.6,0,0,1,6.3,15.9Z';
const HAND_CUFF = 'M16,152H48v56H16a8,8,0,0,1-8-8V160A8,8,0,0,1,16,152Z';

// A six-tooth gear for the lower-right badge, with narrow teeth so the thin stroke doesn't fill the gaps between them.
const GEAR =
  'M195.9,172.3L204.1,172.3L204.9,182.5L216.1,189L225.4,184.5L229.6,191.7L221,197.5L221,210.5L229.6,216.3L225.4,223.5L216.1,219L204.9,225.5L204.1,235.7L195.9,235.7L195.1,225.5L183.9,219L174.6,223.5L170.4,216.3L179,210.5L179,197.5L170.4,191.7L174.6,184.5L183.9,189L195.1,182.5Z';

const MAGNIFIER_LENS = 'M172,204a24,24,0,1,0,48,0a24,24,0,1,0,-48,0Z';

const DRAWINGS: Record<string, CustomIconDrawing> = {
  BankDollar: {
    fill: BANK_ROOF,
    stroke: [BANK_ROOF, BANK_DOLLAR, 'M40,112V176M72,112V176M184,112V176M216,112V176', 'M32,200H224M16,232H240'].join(''),
  },
  // Badge: a trend-up arrow.
  BankTrends: {
    fill: BANK_ROOF,
    stroke: bank({ rightColumnsTo: [152, 144], baseTo: 128 }),
    detail: 'M152,216l32-32,24,24,32-32M208,176h32v32',
  },
  // Badge: a magnifying glass.
  BankDetails: {
    fill: BANK_ROOF + MAGNIFIER_LENS,
    stroke: bank({ rightColumnsTo: [148, 148], baseTo: 136 }),
    detail: MAGNIFIER_LENS + 'M213,221l19,19',
  },
  // A map pin with a dollar coin, standing on a ground oval. The coin is a
  // hole in the fill; the oval's top runs behind the pin's tip.
  MapPinDollar: {
    fill:
      'M128,234C85.1,172.8,50,137,50,98A78,78,0,0,1,206,98C206,137,170.9,172.8,128,234ZM56,216A72,20,0,0,1,200,216A72,20,0,0,1,56,216ZM80,98A48,48,0,0,0,176,98A48,48,0,0,0,80,98Z',
    stroke:
      'M128,234C85.1,172.8,50,137,50,98A78,78,0,0,1,206,98C206,137,170.9,172.8,128,234ZM80,98a48,48,0,1,0,96,0a48,48,0,1,0,-96,0ZM143,78H123a10,10,0,0,0,0,20h10a10,10,0,0,1,0,20H113M128,70v8M128,118v8M154.6,197.4A72,20,0,1,1,101.4,197.4',
  },
  // Two stacked papers; the front one holds a title, a pie chart, a dollar sign,
  // text lines and a bar chart, drawn in the thinner detail stroke.
  FinancialReport: {
    fill: 'M32,44H56V210H200V234H32Z' + REPORT_PIE_SLICE,
    stroke:
      'M64,20H216a8,8,0,0,1,8,8V202a8,8,0,0,1,-8,8H64a8,8,0,0,1,-8,-8V28a8,8,0,0,1,8,-8ZM56,44H40a8,8,0,0,0,-8,8V226a8,8,0,0,0,8,8H192a8,8,0,0,0,8,-8V210',
    detail: REPORT_HEADER + 'M80,148H108M80,166H100M80,186H200M132,186V164M160,186V150M188,186V136',
  },
  // FinancialReport with a magnifying glass.
  ReportReview: {
    fill: BADGED_REPORT_FILL + 'M168,196a24,24,0,1,0,48,0a24,24,0,1,0,-48,0Z',
    stroke: BADGED_REPORT_PAGES,
    detail: BADGED_REPORT_CONTENT + 'M168,196a24,24,0,1,0,48,0a24,24,0,1,0,-48,0ZM209,213l21,21',
  },
  // FinancialReport with a check mark badge.
  ReportCompleted: {
    fill: BADGED_REPORT_FILL,
    stroke: BADGED_REPORT_PAGES,
    detail: BADGED_REPORT_CONTENT + 'M172,206l18,18,38,-38',
  },
  // A slot machine: dome, three 7s on the reels, a side lever and a stand. The
  // frame around the reels is tinted; the reels are a hole in the fill.
  SlotMachine: {
    fill:
      'M40,68H168a24,24,0,0,1,24,24V172a24,24,0,0,1,-24,24H40a24,24,0,0,1,-24,-24V92a24,24,0,0,1,24,-24ZM48,92a8,8,0,0,0,-8,8V164a8,8,0,0,0,8,8H160a8,8,0,0,0,8,-8V100a8,8,0,0,0,-8,-8ZM64,68a40,30,0,0,1,80,0ZM214,58a14,14,0,1,0,28,0a14,14,0,1,0,-28,0Z',
    stroke:
      'M40,68H168a24,24,0,0,1,24,24V172a24,24,0,0,1,-24,24H40a24,24,0,0,1,-24,-24V92a24,24,0,0,1,24,-24ZM64,68a40,30,0,0,1,80,0M192,116h8a8,8,0,0,1,8,8v24a8,8,0,0,1,-8,8h-8M208,140H228V78M214,58a14,14,0,1,0,28,0a14,14,0,1,0,-28,0ZM60,196V214H148V196M40,230H168',
    detail:
      'M48,92H160a8,8,0,0,1,8,8V164a8,8,0,0,1,-8,8H48a8,8,0,0,1,-8,-8V100a8,8,0,0,1,8,-8ZM82.7,92V172M125.3,92V172M53.3,118.0H69.3L58.3,146.0M96,118.0H112L101,146.0M138.7,118.0H154.7L143.7,146.0',
  },
  // A calendar spanned by a double-headed arrow between two end bars: a date
  // range. Drawn on Phosphor's Calendar grid, with its header tinted.
  ReportingWindow: {
    fill: 'M48,40H208a8,8,0,0,1,8,8V80a8,8,0,0,1,-8,8H48a8,8,0,0,1,-8,-8V48a8,8,0,0,1,8,-8Z',
    stroke: 'M48,40H208a8,8,0,0,1,8,8V208a8,8,0,0,1,-8,8H48a8,8,0,0,1,-8,-8V48a8,8,0,0,1,8,-8ZM88,24V56M168,24V56M40,88H216',
    detail: 'M72,124V180M184,124V180M92,152H164M108,136l-16,16,16,16M148,136l16,16-16,16',
  },
  // FinancialReport with a clock badge: not yet approved.
  ReportUnapproved: {
    fill: BADGED_REPORT_FILL + 'M170,204a30,30,0,1,0,60,0a30,30,0,1,0,-60,0Z',
    stroke: BADGED_REPORT_PAGES,
    detail: BADGED_REPORT_CONTENT + 'M170,204a30,30,0,1,0,60,0a30,30,0,1,0,-60,0ZM200,188V204L212,212',
  },
  // FinancialReport with a gear badge.
  ReportSettings: {
    fill: BADGED_REPORT_FILL + GEAR,
    stroke: BADGED_REPORT_PAGES,
    detail: BADGED_REPORT_CONTENT + GEAR + 'M191,204a9,9,0,1,0,18,0a9,9,0,1,0,-18,0Z',
  },
  // Phosphor's Browsers with a bell in the front window.
  PopupNotification: {
    fill:
      'M224,56V168a8,8,0,0,1-8,8H192V88a8,8,0,0,0-8-8H64V56a8,8,0,0,1,8-8H216A8,8,0,0,1,224,56ZM84,176c4,-6,8,-16,8,-28a20,20,0,0,1,40,0c0,12,4,22,8,28Z',
    solid:
      'M216,40H72A16,16,0,0,0,56,56V72H40A16,16,0,0,0,24,88V200a16,16,0,0,0,16,16H184a16,16,0,0,0,16-16V184h16a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,88H184v16H40ZM184,200H40V120H184v80Zm32-32H200V88a16,16,0,0,0-16-16H72V56H216Z',
    detail: 'M84,176c4,-6,8,-16,8,-28a20,20,0,0,1,40,0c0,12,4,22,8,28M78,176H146M106,186a6,6,0,0,0,12,0',
  },
  // A sack tied at the neck, with a dollar sign.
  MoneyBag: {
    fill: 'M100,76C48,108,32,216,128,216S208,108,156,76Z',
    stroke: 'M100,76C48,108,32,216,128,216S208,108,156,76M100,76H156M100,76L84,40C108,50,148,50,172,40L156,76',
    detail: 'M148,124H121a13,13,0,0,0,0,26h14a13,13,0,0,1,0,26H108M128,114v10M128,176v10',
  },
  // A sprout growing from a mound of soil, with a dollar coin for a flower.
  Investment: {
    fill:
      'M68,76a60,60,0,1,0,120,0a60,60,0,1,0,-120,0ZM128,172C116,150,92,148,72,156C86,174,110,180,128,172ZM128,172C140,150,164,148,184,156C170,174,146,180,128,172ZM72,218C72,200,92,190,108,198C118,184,144,184,154,196C172,190,188,200,188,218Z',
    stroke: 'M40,218H216',
    detail:
      'M68,76a60,60,0,1,0,120,0a60,60,0,1,0,-120,0ZM148,41.4A40,40,0,1,0,162.6,96M141,60H123a8,8,0,0,0,0,16h10a8,8,0,0,1,0,16H115M128,54v6M128,92v6M128,142V188M128,172C116,150,92,148,72,156C86,174,110,180,128,172ZM128,172C140,150,164,148,184,156C170,174,146,180,128,172ZM72,218C72,200,92,190,108,198C118,184,144,184,154,196C172,190,188,200,188,218',
  },
  // Phosphor's HandCoins hand offering a key instead of coins.
  HandKey: {
    fill: HAND_CUFF + 'M170,44a30,30,0,1,0,60,0a30,30,0,1,0,-60,0Z',
    solid: HAND,
    stroke: 'M170,44a30,30,0,1,0,60,0a30,30,0,1,0,-60,0ZM178.8,65.2L146,98M156,88l12,12M168,76l12,12',
  },
  // A schedule form with an award seal: an exception granted on it, whether an
  // exclusion or a due-date extension. The page and its tint stop short of the seal.
  Waiver: {
    fill: 'M56,24H184a8,8,0,0,1,8,8V148.2A42,42,0,0,0,171.3,224H56a8,8,0,0,1,-8,-8V32a8,8,0,0,1,8,-8ZM172,190a24,24,0,1,0,48,0a24,24,0,1,0,-48,0Z',
    stroke: 'M192,140.2V32a8,8,0,0,0,-8,-8H56a8,8,0,0,0,-8,8V216a8,8,0,0,0,8,8H159.3',
    detail:
      'M80,54H92a4,4,0,0,1,4,4V70a4,4,0,0,1,-4,4H80a4,4,0,0,1,-4,-4V58a4,4,0,0,1,4,-4ZM112,64H164M80,90H92a4,4,0,0,1,4,4V106a4,4,0,0,1,-4,4H80a4,4,0,0,1,-4,-4V94a4,4,0,0,1,4,-4ZM112,100H164M80,126H92a4,4,0,0,1,4,4V142a4,4,0,0,1,-4,4H80a4,4,0,0,1,-4,-4V130a4,4,0,0,1,4,-4ZM112,136H164M76,176H120M172,190a24,24,0,1,0,48,0a24,24,0,1,0,-48,0ZM182,209.5V238l14,-8,14,8V209.5',
  },
  // A headquarters office building branching to three smaller ones: the
  // organizational hierarchy. Window dots are zero-length strokes with round caps.
  OrgUnits: {
    fill:
      'M106,24H150a6,6,0,0,1,6,6V90a6,6,0,0,1,-6,6H106a6,6,0,0,1,-6,-6V30a6,6,0,0,1,6,-6ZM34,148H70a6,6,0,0,1,6,6V210a6,6,0,0,1,-6,6H34a6,6,0,0,1,-6,-6V154a6,6,0,0,1,6,-6ZM110,148H146a6,6,0,0,1,6,6V210a6,6,0,0,1,-6,6H110a6,6,0,0,1,-6,-6V154a6,6,0,0,1,6,-6ZM186,148H222a6,6,0,0,1,6,6V210a6,6,0,0,1,-6,6H186a6,6,0,0,1,-6,-6V154a6,6,0,0,1,6,-6Z',
    detail:
      'M106,24H150a6,6,0,0,1,6,6V90a6,6,0,0,1,-6,6H106a6,6,0,0,1,-6,-6V30a6,6,0,0,1,6,-6ZM118.7,42h0M118.7,60h0M137.3,42h0M137.3,60h0M121,96V82H135V96M34,148H70a6,6,0,0,1,6,6V210a6,6,0,0,1,-6,6H34a6,6,0,0,1,-6,-6V154a6,6,0,0,1,6,-6ZM44,170.7h0M44,193.3h0M60,170.7h0M60,193.3h0M110,148H146a6,6,0,0,1,6,6V210a6,6,0,0,1,-6,6H110a6,6,0,0,1,-6,-6V154a6,6,0,0,1,6,-6ZM120,170.7h0M120,193.3h0M136,170.7h0M136,193.3h0M186,148H222a6,6,0,0,1,6,6V210a6,6,0,0,1,-6,6H186a6,6,0,0,1,-6,-6V154a6,6,0,0,1,6,-6ZM196,170.7h0M196,193.3h0M212,170.7h0M212,193.3h0M128,96V122M52,122H204M52,122V148M128,122V148M204,122V148',
  },
  // Three kinds of buildings on one ground line: a house, an office tower and a factory.
  OrgUnitTypes: {
    fill:
      'M20,208V129.6L52,104L84,129.6V208ZM106,40H150a6,6,0,0,1,6,6V202a6,6,0,0,1,-6,6H106a6,6,0,0,1,-6,-6V46a6,6,0,0,1,6,-6ZM168,208V128L192,144V128L216,144V128L240,144V208Z',
    stroke: 'M16,216H240',
    detail:
      'M20,208V129.6L52,104L84,129.6V208ZM45,208V192H59V208M106,40H150a6,6,0,0,1,6,6V202a6,6,0,0,1,-6,6H106a6,6,0,0,1,-6,-6V46a6,6,0,0,1,6,-6ZM118.7,65h0M118.7,90h0M118.7,115h0M118.7,140h0M118.7,165h0M137.3,65h0M137.3,90h0M137.3,115h0M137.3,140h0M137.3,165h0M121,208V194H135V208M168,208V128L192,144V128L216,144V128L240,144V208ZM196,208V184H212V208',
  },
  // An office tower with three people standing in front of it; the tower's
  // sides stop above the outer people's heads.
  Institution: {
    fill:
      'M72,24a8,8,0,0,1,8,-8H176a8,8,0,0,1,8,8V150H72ZM110,166a18,18,0,1,0,36,0a18,18,0,1,0,-36,0ZM46,182a14,14,0,1,0,28,0a14,14,0,1,0,-28,0ZM182,182a14,14,0,1,0,28,0a14,14,0,1,0,-28,0Z',
    stroke: 'M72,150V24a8,8,0,0,1,8,-8H176a8,8,0,0,1,8,8V150',
    detail:
      'M104,44h0M104,72h0M104,100h0M104,128h0M152,44h0M152,72h0M152,100h0M152,128h0M110,166a18,18,0,1,0,36,0a18,18,0,1,0,-36,0ZM88,236C88,210,106,196,128,196S168,210,168,236M46,182a14,14,0,1,0,28,0a14,14,0,1,0,-28,0ZM24,236C24,218,40,206,60,206C68,206,75,208,80,211M182,182a14,14,0,1,0,28,0a14,14,0,1,0,-28,0ZM232,236C232,218,216,206,196,206C188,206,181,208,176,211',
  },
};

const path = (attr: Record<string, string>): IconTree => ({ tag: 'path', attr, child: [] });

const STROKE = { fill: 'none', strokeWidth: '16', strokeLinecap: 'round', strokeLinejoin: 'round' };

// Everything but the duotone tint, drawn the same in both weights.
const layers = ({ solid, stroke, detail }: CustomIconDrawing): IconTree[] => [
  ...(solid ? [path({ d: solid, stroke: 'none' })] : []),
  ...(stroke ? [path({ d: stroke, ...STROKE })] : []),
  ...(detail ? [path({ d: detail, ...STROKE, strokeWidth: '12' })] : []),
];

const svg = (child: IconTree[]): IconType => GenIcon({ tag: 'svg', attr: { viewBox: '0 0 256 256', fill: 'currentColor' }, child });

const byName = (render: (drawing: CustomIconDrawing) => IconType) =>
  Object.fromEntries(Object.entries(DRAWINGS).map(([name, drawing]) => [name, render(drawing)]));

export const CUSTOM_ICON_NAMES = Object.keys(DRAWINGS);

export const customDuotone: Record<string, IconType> = byName(drawing =>
  svg([path({ d: drawing.fill, opacity: '0.2', stroke: 'none' }), ...layers(drawing)])
);

export const customRegular: Record<string, IconType> = byName(drawing => svg(layers(drawing)));
